import { Link } from "react-router-dom";
import { themeOf, formatCount, formatShortDate, type Article } from "@/utils/content";

type ArticleCardProps = {
  article: Article;
  variant?: "default" | "row" | "wide";
  rank?: number;
};

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  const theme = themeOf(article.category);

  if (variant === "row") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        {typeof rank === "number" && (
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
              rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
            }`}
          >
            {rank}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${theme.chip}`}>
              {article.categoryName}
            </span>
            <span>{formatCount(article.views)} 阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:grid-cols-5"
      >
        <div className="relative h-48 w-full md:col-span-2 md:h-full md:min-h-[180px]">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 511爆料`}
            className="h-full w-full object-top object-cover"
          />
          {article.hot && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-primary-500 px-2 py-1 text-xs font-semibold text-background-50">
              <i className="ri-fire-line" />
              热传
            </span>
          )}
        </div>
        <div className="flex flex-col justify-between gap-3 p-5 md:col-span-3">
          <div>
            <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${theme.chip}`}>
              {article.categoryName}
            </span>
            <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {article.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {formatShortDate(article.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 511爆料`}
          className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${theme.chip}`}>
          {article.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" />
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatCount(article.views)}
          </span>
          <span className="ml-auto flex items-center gap-1">
            <i className="ri-time-line" />
            {formatShortDate(article.date)}
          </span>
        </div>
      </div>
    </Link>
  );
}